import { motion } from "framer-motion";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import { TRILOGY_STEPS } from "@/lib/cursusMeta";

/**
 * Pedagogical trilogy : Analyser · Appliquer · Évaluer.
 *
 * variant:
 *   - "full"    : full section with title (used on /a-propos, /cursus/:slug)
 *   - "compact" : trimmed version (used on /formations/:slug)
 */
export default function MethodeTrilogy({ variant = "full", theme = "light" }) {
    const dark = theme === "dark";
    const compact = variant === "compact";

    const wrapperBg = dark ? "bg-brand-ink text-white" : "bg-brand-sand text-brand-ink";
    const titleColor = dark ? "text-white" : "text-brand-ink";
    const accentColor = dark ? "text-white/55" : "text-brand-smoke";
    const cardBg = dark
        ? "border-white/10 bg-white/[0.03] hover:border-brand-orange/40"
        : "border-brand-line bg-white shadow-premium hover:-translate-y-1";

    return (
        <section
            className={`relative overflow-hidden ${wrapperBg} ${compact ? "py-16 md:py-20" : "py-24 md:py-32"}`}
            data-testid={`methode-trilogy-${variant}`}
        >
            {dark && <div className="absolute inset-0 grain pointer-events-none opacity-25" />}
            <div className="relative mx-auto max-w-7xl px-6">
                <Reveal>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                        Notre méthode pédagogique
                    </span>
                    <h2
                        className={`mt-5 max-w-4xl font-display font-normal leading-[0.95] tracking-tightest ${titleColor} ${
                            compact ? "text-3xl md:text-4xl lg:text-5xl" : "text-[40px] sm:text-5xl lg:text-[64px]"
                        }`}
                    >
                        Notre méthode <em className={`italic ${accentColor}`}>en 3 temps.</em>
                    </h2>
                    {!compact && (
                        <p className={`mt-5 max-w-2xl text-base leading-relaxed md:text-lg ${dark ? "text-white/65" : "text-brand-ink/70"}`}>
                            Chaque formation Cursus Physio suit une trilogie pédagogique éprouvée. Une logique simple, exigeante, profondément efficace.
                        </p>
                    )}
                </Reveal>

                <div className="relative mt-14">
                    {/* Connecting line, drawn-on-scroll */}
                    <motion.div
                        aria-hidden="true"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, margin: "-20%" }}
                        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                        className={`pointer-events-none absolute left-[8%] right-[8%] top-12 hidden h-[2px] origin-left ${
                            dark ? "bg-brand-orange/35" : "bg-brand-orange/40"
                        } md:block`}
                    />

                    <StaggerGroup stagger={0.14} className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        {TRILOGY_STEPS.map((step) => (
                            <StaggerItem key={step.num}>
                                <article
                                    className={`relative h-full overflow-hidden rounded-3xl border p-8 transition-all duration-500 ${cardBg}`}
                                    data-testid={`trilogy-step-${step.num}`}
                                >
                                    {/* Watermark numeral */}
                                    <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute -right-4 -top-6 select-none font-display italic leading-none text-brand-orange/[0.12]"
                                        style={{ fontSize: compact ? "120px" : "150px", letterSpacing: "-0.04em" }}
                                    >
                                        {step.num}
                                    </span>

                                    <div className="relative">
                                        <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                                            {step.num} · Étape
                                        </div>
                                        <h3 className={`mt-3 font-display ${titleColor} ${compact ? "text-2xl" : "text-3xl"}`}>
                                            {step.label}
                                        </h3>
                                        <p className={`mt-4 text-sm leading-relaxed md:text-base ${dark ? "text-white/65" : "text-brand-ink/70"}`}>
                                            {step.text}
                                        </p>
                                    </div>
                                </article>
                            </StaggerItem>
                        ))}
                    </StaggerGroup>
                </div>
            </div>
        </section>
    );
}
